// src/pages/Home/CommonSections/DownloadCallToActionSection.js

import React, { useState, useEffect } from 'react';
import {
    Button,
    Spinner,
    Modal,
    Row,
    Col,
    Tooltip,
    OverlayTrigger,
    Alert,
} from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faApple,
    faWindows,
    faLinux
} from '@fortawesome/free-brands-svg-icons';
import {
    faDownload,
    faCheckCircle,
    faShieldAlt,
    faList,
    faShare,
    faCopy,
    faCheck
} from '@fortawesome/free-solid-svg-icons';
import { isMobile } from 'react-device-detect';
import { publicDownloadSoftware, logWindowsDownload } from '../../../contexts/GcfAPIs';
import UseWindowDimensions from '../../../sharedcomponents/UseWindowDimensions';

import './css/DownloadCallToActionSection.css';

const DownloadCallToActionSection = ({ 
    mainHeading = "Download TensorPilot",
    subHeading = "Your Personal AI",
    isEnterprise = false,
    /** When true, omit the in-section title (e.g. dedicated /download page supplies a hero heading). */
    hideTitle = false,
}) => {
    const [isDownloadingIntel, setIsDownloadingIntel] = useState(false);
    const [isDownloadingArm, setIsDownloadingArm] = useState(false);
    const [isLoggingWindows, setIsLoggingWindows] = useState(false);
    // Linux download states
    const [isDownloadingLinuxX64Deb, setIsDownloadingLinuxX64Deb] = useState(false);
    const [isDownloadingLinuxX64Rpm, setIsDownloadingLinuxX64Rpm] = useState(false);
    const [isDownloadingLinuxArm64Deb, setIsDownloadingLinuxArm64Deb] = useState(false);
    const [isDownloadingLinuxArm64Rpm, setIsDownloadingLinuxArm64Rpm] = useState(false);
    
    const [alertMessage, setAlertMessage] = useState(null);
    const [alertVariant, setAlertVariant] = useState('success');
    const [showAlertModal, setShowAlertModal] = useState(false);

    const [showPrivacyFeatures, setShowPrivacyFeatures] = useState(false);
    
    // Mobile detection and share functionality
    const { width } = UseWindowDimensions();
    const [isMobileView, setIsMobileView] = useState(false);
    const [linkCopied, setLinkCopied] = useState(false);

    useEffect(() => {
        // Check if mobile device or small screen
        setIsMobileView(isMobile || width <= 768);
    }, [width]);

    const startDownload = async (type) => {
        try {
            if (type === 'MacIntel') {
                setIsDownloadingIntel(true);
                await handleDownloadMac('intel');
            } else if (type === 'MacARM') {
                setIsDownloadingArm(true);
                await handleDownloadMac('arm');
            } else if (type === 'Windows') {
                setIsLoggingWindows(true);
                await handleDownloadWindows();
            } else if (type === 'LinuxX64Deb') {
                setIsDownloadingLinuxX64Deb(true);
                await handleDownloadLinux('x64', 'deb');
            } else if (type === 'LinuxX64Rpm') {
                setIsDownloadingLinuxX64Rpm(true);
                await handleDownloadLinux('x64', 'rpm');
            } else if (type === 'LinuxArm64Deb') {
                setIsDownloadingLinuxArm64Deb(true);
                await handleDownloadLinux('arm64', 'deb');
            } else if (type === 'LinuxArm64Rpm') {
                setIsDownloadingLinuxArm64Rpm(true);
                await handleDownloadLinux('arm64', 'rpm');
            }
        } catch (error) {
            console.error('Download error:', error);
            setAlertMessage(error.message || 'Failed to start the download. Please try again later.');
            setAlertVariant('danger');
            setShowAlertModal(true);
        } finally {
            setIsDownloadingIntel(false);
            setIsDownloadingArm(false);
            setIsLoggingWindows(false);
            setIsDownloadingLinuxX64Deb(false);
            setIsDownloadingLinuxX64Rpm(false);
            setIsDownloadingLinuxArm64Deb(false);
            setIsDownloadingLinuxArm64Rpm(false);
        }
    };

    const handleDownloadMac = async (arch) => {
        const response = await publicDownloadSoftware('Mac', arch);
        
        const { url } = response;
        const a = document.createElement('a');
        a.href = url;
        a.download = '';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);

        setAlertMessage('Download started successfully.');
        setAlertVariant('success');
        setShowAlertModal(true);
    };

    const handleDownloadLinux = async (arch, format) => {
        const response = await publicDownloadSoftware('Linux', arch, format);
        
        const { url } = response;
        const a = document.createElement('a');
        a.href = url;
        a.download = '';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);

        setAlertMessage(`Linux ${format.toUpperCase()} download started successfully.`);
        setAlertVariant('success');
        setShowAlertModal(true);
    };

    const handleDownloadWindows = async () => {
        try {
            await logWindowsDownload();

            const newWindow = window.open(
                'https://apps.microsoft.com/store/detail/9P7MRSZSCWNN?cid=DevShareMCLPCS',
                '_blank'
            );

            if (newWindow === null) {
                setAlertMessage(
                    <>
                        <p>
                            Pop-up was blocked! Please allow pop-ups for this site or click the link
                            below to navigate to Windows Store.
                        </p>
                        <p>
                            <a
                                href="https://apps.microsoft.com/store/detail/9P7MRSZSCWNN?cid=DevShareMCLPCS"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                Go to Microsoft Store
                            </a>
                        </p>
                    </>
                );
                setAlertVariant('danger');
            } else {
                setAlertMessage('Redirecting to Windows Store...');
                setAlertVariant('success');
            }
            setShowAlertModal(true);
        } catch (error) {
            console.error('Error logging the Windows download event:', error);
            setAlertMessage(
                <>
                    <p>
                        Something went wrong, you will still be redirected. If the redirection didn't
                        work, please click the link below:
                    </p>
                    <p>
                        <a
                            href="https://apps.microsoft.com/store/detail/9P7MRSZSCWNN?cid=DevShareMCLPCS"
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            Go to Microsoft Store
                        </a>
                    </p>
                </>
            );
            setAlertVariant('warning');
            setShowAlertModal(true);

            const newWindowFallback = window.open(
                'https://apps.microsoft.com/store/detail/9P7MRSZSCWNN?cid=DevShareMCLPCS',
                '_blank'
            );

            if (newWindowFallback === null) {
                setAlertMessage(
                    <>
                        <p>
                            Pop-up was blocked! Please allow pop-ups for this site and click the link below
                            to proceed.
                        </p>
                        <p>
                            <a
                                href="https://apps.microsoft.com/store/detail/9P7MRSZSCWNN?cid=DevShareMCLPCS"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                Go to Microsoft Store
                            </a>
                        </p>
                    </>
                );
                setAlertVariant('danger');
                setShowAlertModal(true);
            }
        }
    };

    // Mobile share/email functionality
    const getDownloadPageUrl = () => {
        return window.location.origin + '/download';
    };

    const handleCopyLink = async () => {
        const url = getDownloadPageUrl();
        try {
            await navigator.clipboard.writeText(url);
            setLinkCopied(true);
            setTimeout(() => setLinkCopied(false), 2000);
        } catch (err) {
            // Fallback for older browsers
            const textArea = document.createElement('textarea');
            textArea.value = url;
            document.body.appendChild(textArea);
            textArea.select();
            try {
                document.execCommand('copy');
                setLinkCopied(true);
                setTimeout(() => setLinkCopied(false), 2000);
            } catch (fallbackErr) {
                console.error('Failed to copy link:', fallbackErr);
                setAlertMessage('Failed to copy link. Please try again.');
                setAlertVariant('danger');
                setShowAlertModal(true);
            }
            document.body.removeChild(textArea);
        }
    };

    const handleWebShare = async () => {
        const url = getDownloadPageUrl();
        const shareData = {
            title: 'Download TensorPilot',
            text: 'Check out TensorPilot - Free AI software for your desktop!',
            url: url,
        };

        try {
            if (navigator.share && navigator.canShare && navigator.canShare(shareData)) {
                await navigator.share(shareData);
            } else {
                // Fallback to copy link if share is not available
                handleCopyLink();
            }
        } catch (err) {
            if (err.name !== 'AbortError') {
                console.error('Error sharing:', err);
                // Fallback to copy link
                handleCopyLink();
            }
        }
    };

    return (
        <div
            className={`download-section my-4${hideTitle ? ' download-section--no-page-title' : ''}`}
            id="download-section"
        >
            {/* Mobile Banner */}
            {isMobileView && (
                <Row className="mb-4">
                    <Col>
                        <div className="mobile-download-banner">
                            <div className="mobile-banner-content">
                                <div className="mobile-banner-icon">
                                    <FontAwesomeIcon icon={faDownload} />
                                </div>
                                <h3 className="mobile-banner-title">Download on Desktop</h3>
                                <p className="mobile-banner-text">
                                    TensorPilot is available for Windows, macOS, and Linux. Save this link to download later on your computer.
                                </p>
                                <div className="mobile-share-buttons">
                                    {navigator.share && (
                                        <button
                                            className="mobile-share-btn mobile-share-btn-primary"
                                            onClick={handleWebShare}
                                        >
                                            <FontAwesomeIcon icon={faShare} />
                                            <span>Share</span>
                                        </button>
                                    )}
                                    <button
                                        className={`mobile-share-btn mobile-share-btn-secondary ${linkCopied ? 'copied' : ''}`}
                                        onClick={handleCopyLink}
                                    >
                                        <FontAwesomeIcon icon={linkCopied ? faCheck : faCopy} />
                                        <span>{linkCopied ? 'Copied!' : 'Copy'}</span>
                                    </button>
                                </div>
                            </div>
                        </div>
                    </Col>
                </Row>
            )}

            {/* Header (hidden on /download when the page hero shows the title) */}
            {!hideTitle && (
                <Row className="text-center mt-3">
                    <Col>
                        {isEnterprise ? (
                            <h2 className="section-title" style={{ marginBottom: '0.5rem' }}>
                                {mainHeading.split(' ').map((word, idx, arr) => 
                                    idx === arr.length - 1 ? (
                                        <span key={idx}><span className="title-accent">{word}</span></span>
                                    ) : (
                                        <span key={idx}>{word} </span>
                                    )
                                )}
                            </h2>
                        ) : (
                            <p className="display-4-5 gradient-text">{mainHeading}</p>
                        )}
                        <p className="subtitle-text">{subHeading}</p>
                    </Col>
                </Row>
            )}

            {/* Privacy/Features Button */}
            <Row className={`justify-content-center mb-4${hideTitle ? ' mt-1 mt-md-2' : ''}`}>
                <Col xs={12} md={8} lg={6} className="text-center">
                    <Button
                        size='sm'
                        variant="outline-info"
                        onClick={() => setShowPrivacyFeatures(true)}
                    >
                        <FontAwesomeIcon icon={faShieldAlt} className="mr-2" />
                        Privacy & Data
                    </Button>
                </Col>
            </Row>

            <div className="download-platforms-wrap">
                {/* Row 1: Mac Intel, Mac ARM, Windows */}
                <div className="custom-flow download-platform-row">
                    <div className="flow-item flow-item--mac-intel">
                        <FontAwesomeIcon
                            icon={faApple}
                            size="3x"
                            className="mb-3 apple-icon"
                        />
                        <div className="trusted-badges mb-2">
                            <OverlayTrigger
                                placement="top"
                                overlay={<Tooltip id="tooltip-apple-intel">Verified by Apple</Tooltip>}
                            >
                                <FontAwesomeIcon
                                    icon={faCheckCircle}
                                    color="green"
                                    size="sm"
                                    aria-label="Verified by Apple"
                                />
                            </OverlayTrigger>
                        </div>
                        <p className="public-download-name">Mac OS (Intel)</p>

                        <Button
                            className="mt-auto btn-download"
                            onClick={() => startDownload('MacIntel')}
                            disabled={isDownloadingIntel || isLoggingWindows}
                            aria-label="Download Mac OS (Intel)"
                        >
                            {isDownloadingIntel ? (
                                <>
                                    <Spinner
                                        as="span"
                                        animation="border"
                                        size="sm"
                                        role="status"
                                        aria-hidden="true"
                                    />{' '}
                                    Downloading...
                                </>
                            ) : (
                                <>
                                    <FontAwesomeIcon icon={faDownload} /> Download
                                </>
                            )}
                        </Button>
                    </div>

                    <div className="flow-item flow-item--mac-arm">
                        <FontAwesomeIcon
                            icon={faApple}
                            size="3x"
                            className="mb-3 apple-icon"
                        />
                        <div className="trusted-badges mb-2">
                            <OverlayTrigger
                                placement="top"
                                overlay={<Tooltip id="tooltip-apple-arm">Verified by Apple</Tooltip>}
                            >
                                <FontAwesomeIcon
                                    icon={faCheckCircle}
                                    color="green"
                                    size="sm"
                                    aria-label="Verified by Apple"
                                />
                            </OverlayTrigger>
                        </div>
                        <p className="public-download-name">Mac OS (ARM)</p>

                        <Button
                            className="mt-auto btn-download"
                            onClick={() => startDownload('MacARM')}
                            disabled={isDownloadingArm || isLoggingWindows}
                            aria-label="Download Mac OS (ARM)"
                        >
                            {isDownloadingArm ? (
                                <>
                                    <Spinner
                                        as="span"
                                        animation="border"
                                        size="sm"
                                        role="status"
                                        aria-hidden="true"
                                    />{' '}
                                    Downloading...
                                </>
                            ) : (
                                <>
                                    <FontAwesomeIcon icon={faDownload} /> Download
                                </>
                            )}
                        </Button>
                    </div>

                    <div className="flow-item flow-item--windows">
                        <FontAwesomeIcon
                            icon={faWindows}
                            size="3x"
                            className="mb-3 windows-icon"
                        />
                        <div className="trusted-badges mb-2">
                            <OverlayTrigger
                                placement="top"
                                overlay={<Tooltip id="tooltip-windows">Verified by Microsoft</Tooltip>}
                            >
                                <FontAwesomeIcon
                                    icon={faCheckCircle}
                                    color="green"
                                    size="sm"
                                    aria-label="Verified by Microsoft"
                                />
                            </OverlayTrigger>
                        </div>
                        <p className="public-download-name">Windows</p>

                        <Button
                            className="mt-auto btn-download"
                            onClick={() => startDownload('Windows')}
                            disabled={isDownloadingIntel || isDownloadingArm || isLoggingWindows}
                            aria-label="Download Windows"
                        >
                            {isLoggingWindows ? (
                                <>
                                    <Spinner
                                        as="span"
                                        animation="border"
                                        size="sm"
                                        role="status"
                                        aria-hidden="true"
                                    />{' '}
                                    Redirecting...
                                </>
                            ) : (
                                <>
                                    <FontAwesomeIcon icon={faDownload} /> Windows Store
                                </>
                            )}
                        </Button>
                    </div>
                </div>

                {/* Row 2: Linux x64, Linux ARM64 */}
                <div className="custom-flow download-platform-row">
                    <div className="flow-item linux-card-wide flow-item--linux-x64">
                        <FontAwesomeIcon
                            icon={faLinux}
                            size="3x"
                            className="mb-3 linux-icon"
                        />
                        <div className="trusted-badges mb-2">
                            <OverlayTrigger
                                placement="top"
                                overlay={<Tooltip id="tooltip-linux-x64">Open Source</Tooltip>}
                            >
                                <FontAwesomeIcon
                                    icon={faCheckCircle}
                                    color="green"
                                    size="sm"
                                    aria-label="Open Source"
                                />
                            </OverlayTrigger>
                        </div>
                        <p className="public-download-name">Linux x64</p>
                        <p className="text-muted small mb-3">Intel / AMD processors</p>

                        <div className="linux-btn-row">
                            <Button
                                className="btn-download-linux deb"
                                onClick={() => startDownload('LinuxX64Deb')}
                                disabled={isDownloadingLinuxX64Deb}
                                aria-label="Download Linux x64 DEB"
                            >
                                {isDownloadingLinuxX64Deb ? (
                                    <Spinner as="span" animation="border" size="sm" />
                                ) : (
                                    <>
                                        <FontAwesomeIcon icon={faDownload} /> .deb
                                    </>
                                )}
                            </Button>
                            <Button
                                className="btn-download-linux rpm"
                                onClick={() => startDownload('LinuxX64Rpm')}
                                disabled={isDownloadingLinuxX64Rpm}
                                aria-label="Download Linux x64 RPM"
                            >
                                {isDownloadingLinuxX64Rpm ? (
                                    <Spinner as="span" animation="border" size="sm" />
                                ) : (
                                    <>
                                        <FontAwesomeIcon icon={faDownload} /> .rpm
                                    </>
                                )}
                            </Button>
                        </div>
                        <div className="distro-hints mt-2">
                            <small className="text-muted">.deb → Ubuntu, Debian, Mint</small>
                            <small className="text-muted">.rpm → Fedora, RHEL, CentOS</small>
                        </div>
                    </div>

                    <div className="flow-item linux-card-wide flow-item--linux-arm64">
                        <FontAwesomeIcon
                            icon={faLinux}
                            size="3x"
                            className="mb-3 linux-icon"
                        />
                        <div className="trusted-badges mb-2">
                            <OverlayTrigger
                                placement="top"
                                overlay={<Tooltip id="tooltip-linux-arm">Open Source</Tooltip>}
                            >
                                <FontAwesomeIcon
                                    icon={faCheckCircle}
                                    color="green"
                                    size="sm"
                                    aria-label="Open Source"
                                />
                            </OverlayTrigger>
                        </div>
                        <p className="public-download-name">Linux ARM64</p>
                        <p className="text-muted small mb-3">Raspberry Pi, Apple Silicon VMs</p>

                        <div className="linux-btn-row">
                            <Button
                                className="btn-download-linux deb"
                                onClick={() => startDownload('LinuxArm64Deb')}
                                disabled={isDownloadingLinuxArm64Deb}
                                aria-label="Download Linux ARM64 DEB"
                            >
                                {isDownloadingLinuxArm64Deb ? (
                                    <Spinner as="span" animation="border" size="sm" />
                                ) : (
                                    <>
                                        <FontAwesomeIcon icon={faDownload} /> .deb
                                    </>
                                )}
                            </Button>
                            <Button
                                className="btn-download-linux rpm"
                                onClick={() => startDownload('LinuxArm64Rpm')}
                                disabled={isDownloadingLinuxArm64Rpm}
                                aria-label="Download Linux ARM64 RPM"
                            >
                                {isDownloadingLinuxArm64Rpm ? (
                                    <Spinner as="span" animation="border" size="sm" />
                                ) : (
                                    <>
                                        <FontAwesomeIcon icon={faDownload} /> .rpm
                                    </>
                                )}
                            </Button>
                        </div>
                        <div className="distro-hints mt-2">
                            <small className="text-muted">.deb → Ubuntu, Debian (ARM)</small>
                            <small className="text-muted">.rpm → Fedora, RHEL (ARM)</small>
                        </div>
                    </div>
                </div>
            </div>

            <Row className="justify-content-center mt-4">
                <Col xs={12} md={10} lg={8} className="text-center">
                    <small className="text-muted download-terms-note">
                        By downloading, you agree to our{' '}
                        <a href="/terms-and-conditions" target="_blank" rel="noopener noreferrer">
                            Terms &amp; Conditions
                        </a>{' '}
                        and{' '}
                        <a href="/privacy-policy" target="_blank" rel="noopener noreferrer">
                            Privacy Policy
                        </a>
                        .
                    </small>
                </Col>
            </Row>

            <Modal show={showPrivacyFeatures} onHide={() => setShowPrivacyFeatures(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Privacy & Data</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <h5>
                        <FontAwesomeIcon icon={faShieldAlt} className="mr-2" />
                        {isEnterprise ? "Enterprise Data Sovereignty" : "Your Privacy, Our Priority"}
                    </h5>
                    <p>
                        {isEnterprise 
                            ? "TensorPilot's local-first architecture keeps workspaces on device by default, with AI requests going directly to provider APIs using your enterprise keys. Optional Pro encrypted cloud backup is off until you enable it."
                            : "At TensorPilot, your privacy and data security are at the heart of our mission. We believe your data belongs to you, and we are committed to protecting it at every step."
                        }
                    </p>
                    <ul>
                        {isEnterprise ? (
                            <>
                                <li><strong>Sovereign Architecture:</strong> Workspaces and conversations are stored locally in SQLite; API keys use OS-native keychains (macOS Keychain, Windows Credential Manager). Optional Pro cloud backup encrypts on device before upload.</li>
                                <li><strong>No AI proxy:</strong> Model calls go directly from your device to provider APIs (BYOK). Optional Pro cloud backup stores only ciphertext we cannot decrypt.</li>
                                <li><strong>Enterprise Key Management:</strong> Support for Individual BYOK, Team Key Pools (IT-distributed), and Centralized Key Vault Integration (HashiCorp Vault or AWS Secrets Manager).</li>
                                <li><strong>Granular Cost Tracking:</strong> Token-level cost tracking per workspace/project enables accurate bill-back of AI costs as disbursements for law firms and consultancies.</li>
                                <li><strong>Compliance Ready:</strong> Local-first by default; assess HIPAA, GDPR, and CCPA based on your workflows, provider BAAs, and whether you enable encrypted cloud backup.</li>
                                <li><strong>Enterprise rollout:</strong> Installers for all platforms with configuration profiles for silent installation and team key distribution.</li>
                            </>
                        ) : (
                            <>
                                <li><strong>Local-First by Default:</strong> Chats and workspaces stay on your device. TensorPilot Pro includes optional end-to-end encrypted cloud backup (off by default) for recovery and new-device restore.</li>
                                <li><strong>Usage Tracking for You:</strong> We track your input and output tokens, as well as usage and cost data, so you can monitor your own activity and spending. This data is visible only to you and is not shared with third parties.</li>
                                <li><strong>API Key Security:</strong> Your API keys (for Gemini, Claude, OpenAI, Grok, and other AI providers) are stored securely at the OS level—using Mac Keychain or Windows Credential Manager. They are never transmitted to our servers.</li>
                                <li><strong>Multiple AI Providers:</strong> Enjoy seamless integration with leading AI providers including Gemini, Claude, OpenAI, Grok, and more. All AI processing is performed securely and in compliance with each provider's data usage policies.</li>
                                <li><strong>User Control:</strong> You have full control to manage, export, or delete your data at any time from within the app.</li>
                                <li><strong>Compliance:</strong> We adhere to global privacy standards, including GDPR and CCPA, to ensure your rights are respected.</li>
                            </>
                        )}
                    </ul>
                    <h5 className="mt-4">
                        <FontAwesomeIcon icon={faList} className="mr-2" />
                        Key Features
                    </h5>
                    <ul>
                        <li>Free to download and use basic features.</li>
                        <li>Advanced AI capabilities with seamless integration of Gemini, Claude, and other providers.</li>
                        <li>Optional cloud backup for your chats and data.</li>
                        <li>Regular updates and new features for enhanced productivity.</li>
                        <li>Modern, intuitive interface designed for privacy and ease of use.</li>
                        <li>Dedicated support and transparent communication.</li>
                    </ul>
                    <p>
                        For full details, please review our{' '}
                        <a
                            href="/privacy-policy"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="a-onclick"
                        >
                            Privacy Policy
                        </a>{' '}and{' '}
                        <a
                            href="/terms-and-conditions"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="a-onclick"
                        >
                            Terms & Conditions
                        </a>.
                    </p>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowPrivacyFeatures(false)}>
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>

            <Modal
                show={showAlertModal}
                onHide={() => {
                    setShowAlertModal(false);
                    setAlertMessage(null);
                }}
                centered
            >
                <Modal.Header closeButton>
                    <Modal.Title>Notification</Modal.Title>
                </Modal.Header>
                <Modal.Body>{alertMessage}</Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="secondary"
                        onClick={() => {
                            setShowAlertModal(false);
                            setAlertMessage(null);
                        }}
                    >
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>

        </div>
    );
};

export default DownloadCallToActionSection;